import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import clsx from "clsx";
import type { Device } from "../../types";

interface Props {
  devices: Device[];
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder?: string;
  className?: string;
}

export function DeviceCombobox({ devices, value, onChange, placeholder, className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => devices.find((d) => d.id === value) || null,
    [devices, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = !q
      ? devices
      : devices.filter(
          (d) => d.name.toLowerCase().includes(q) || d.host.toLowerCase().includes(q),
        );
    return [...base].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [devices, query]);

  useEffect(() => {
    setHighlighted(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const item = listRef.current?.querySelector<HTMLElement>(`[data-idx="${highlighted}"]`);
    item?.scrollIntoView({ block: "nearest" });
  }, [highlighted, open]);

  const commit = (id: number | null) => {
    onChange(id);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && filtered[highlighted]) commit(filtered[highlighted].id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const displayValue = open ? query : selected?.name || "";

  return (
    <div ref={rootRef} className={clsx("relative", className)}>
      <div className="relative">
        <Search
          size={12}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder || "Buscar..."}
          className="bg-bg border border-line rounded pl-6 pr-6 py-1 text-sm text-text placeholder-muted focus:outline-none focus:border-accent w-56"
        />
        {(selected || query) && (
          <button
            type="button"
            onClick={() => commit(null)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted hover:text-text"
            title="Limpiar"
          >
            <X size={12} />
          </button>
        )}
      </div>
      {open && (
        <div
          ref={listRef}
          className="absolute z-30 mt-1 w-72 max-h-72 overflow-y-auto bg-panel border border-line rounded shadow-lg"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted">Sin resultados</div>
          ) : (
            filtered.map((d, i) => (
              <button
                key={d.id}
                type="button"
                data-idx={i}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(d.id);
                }}
                onMouseEnter={() => setHighlighted(i)}
                className={clsx(
                  "w-full text-left px-3 py-1.5 text-xs flex items-center justify-between gap-2",
                  i === highlighted ? "bg-bg" : "hover:bg-bg",
                  value === d.id ? "text-accent" : "text-text",
                )}
              >
                <span className="truncate">{d.name}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <span className="text-muted">{d.host}</span>
                  <span
                    className={clsx(
                      "inline-block w-2 h-2 rounded-full",
                      d.is_online ? "bg-green-500" : "bg-red-500",
                    )}
                    title={d.is_online ? "En línea" : "Fuera de línea"}
                  />
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
